import { LegalMention } from '@entries/legal-mention'
import IconCb from '@fortuneo/ui-icons/general/IconCb'
import IconPublish from '@fortuneo/ui-icons/general/IconPublish'
import IconRoomService from '@fortuneo/ui-icons/general/IconRoomService'
import IconVerified from '@fortuneo/ui-icons/general/IconVerified'
import IconZeroEuro from '@fortuneo/ui-icons/general/IconZeroEuro'
import React, { type ElementType, type ReactNode } from 'react'

export type CardAdvantage = {
  Icon: ElementType
  content: ReactNode
}

type CardData = {
  isPopular?: boolean
  mobileMenu: {
    id: string
    label: string
  }
  general: {
    name: ReactNode
    assetName: string
    underCondition: ReactNode
    link: string
    clientLink: string
  }
  advantages: CardAdvantage[]
}

type CardDataFunc = (
  legalMention: Queries.CardComparatorLightQuery['legalMention']
) => CardData

export const fosfo: CardDataFunc = (legalMention) => ({
  mobileMenu: {
    id: 'fosfo',
    label: 'Fosfo',
  },
  general: {
    name: 'Fosfo Mastercard',
    assetName: 'fosfo',
    underCondition: <>Sans conditions de revenus</>,
    link: '/compte-bancaire/carte-fosfo-mastercard',
    clientLink: '/compte-bancaire/carte-fosfo-mastercard-prive',
  },
  advantages: [
    {
      Icon: IconZeroEuro,
      content: (
        <p className="text-085 leading-alternate">
          Gratuite
          <LegalMention legalMention={legalMention} />, sous réserve d’un
          paiement carte par mois
        </p>
      ),
    },
    {
      Icon: IconCb,
      content: <p className="text-085 leading-alternate">Débit immédiat</p>,
    },
    {
      Icon: IconVerified,
      content: (
        <p className="text-085 leading-alternate">
          Garanties d’assurance quotidien
        </p>
      ),
    },
  ],
})

export const fosfoShared: CardDataFunc = (legalMention) => ({
  mobileMenu: {
    id: 'fosfo',
    label: 'Fosfo',
  },
  general: {
    name: 'Fosfo Mastercard',
    assetName: 'fosfo',
    underCondition: <>Sans conditions de revenus</>,
    link: '/compte-bancaire/carte-fosfo-mastercard',
    clientLink: '/compte-bancaire/carte-fosfo-mastercard-prive',
  },
  advantages: [
    {
      Icon: IconZeroEuro,
      content: (
        <p className="text-085 leading-alternate">
          Gratuite
          <LegalMention legalMention={legalMention} />, à partir d’un paiement
          carte par mois, par carte associée au compte joint
        </p>
      ),
    },
    {
      Icon: IconCb,
      content: <p className="text-085 leading-alternate">Débit immédiat</p>,
    },
    {
      Icon: IconVerified,
      content: (
        <p className="text-085 leading-alternate">
          Garanties d’assurance quotidien
        </p>
      ),
    },
  ],
})

export const gold: CardDataFunc = (legalMention) => ({
  isPopular: true,
  mobileMenu: {
    id: 'gold',
    label: 'Gold',
  },
  general: {
    name: (
      <>
        Gold
        <br />
        CB Mastercard
      </>
    ),
    assetName: 'gold',
    underCondition: <>Dès 1 800 € de revenus nets mensuels</>,
    link: '/compte-bancaire/carte-gold-mastercard',
    clientLink: '/compte-bancaire/carte-gold-mastercard-prive',
  },
  advantages: [
    {
      Icon: IconZeroEuro,
      content: (
        <p className="text-085 leading-alternate">
          Gratuite
          <LegalMention legalMention={legalMention} />, sous réserve d’un
          paiement carte par mois
        </p>
      ),
    },
    {
      Icon: IconCb,
      content: (
        <p className="text-085 leading-alternate">
          Débit immédiat ou différé dès 6 mois d’ancienneté
        </p>
      ),
    },
    {
      Icon: IconVerified,
      content: (
        <p className="text-085 leading-alternate">
          Garanties d'assurance et d'assistance renforcées
        </p>
      ),
    },
    {
      Icon: IconPublish,
      content: (
        <p className="text-085 leading-alternate">
          Plafond de paiement/retrait élevés
        </p>
      ),
    },
  ],
})

export const goldShared: CardDataFunc = (legalMention) => ({
  isPopular: true,
  mobileMenu: {
    id: 'gold',
    label: 'Gold',
  },
  general: {
    name: (
      <>
        Gold
        <br />
        CB Mastercard
      </>
    ),
    assetName: 'gold',
    underCondition: (
      <>
        Dès 2 700 € de revenus nets mensuels à justifier à deux à la
        souscription
      </>
    ),
    link: '/compte-bancaire/carte-gold-mastercard',
    clientLink: '/compte-bancaire/carte-gold-mastercard-prive',
  },
  advantages: [
    {
      Icon: IconZeroEuro,
      content: (
        <p className="text-085 leading-alternate">
          Gratuite
          <LegalMention legalMention={legalMention} />, à partir d’un paiement
          carte par mois, par carte associée au compte joint
        </p>
      ),
    },
    {
      Icon: IconCb,
      content: (
        <p className="text-085 leading-alternate">
          Débit immédiat ou différé dès 6 mois d’ancienneté
        </p>
      ),
    },
    {
      Icon: IconVerified,
      content: (
        <p className="text-085 leading-alternate">
          Garanties d'assurance et d'assistance renforcées
        </p>
      ),
    },
    {
      Icon: IconPublish,
      content: (
        <p className="text-085 leading-alternate">
          Plafond de paiement/retrait élevés
        </p>
      ),
    },
  ],
})

export const worldElite: CardDataFunc = (legalMention) => ({
  mobileMenu: {
    id: 'world-elite',
    label: 'World Elite',
  },
  general: {
    name: 'World Elite CB Mastercard',
    assetName: 'worldElite',
    underCondition: <>Dès 4 000 € de revenus nets mensuels</>,
    link: '/compte-bancaire/carte-world-elite-mastercard',
    clientLink: '/compte-bancaire/carte-world-elite-mastercard-prive',
  },
  advantages: [
    {
      Icon: IconZeroEuro,
      content: (
        <p className="text-085 leading-alternate">
          Gratuite
          <LegalMention legalMention={legalMention} />, à partir d’un versement
          mensuel de <span className="inline-block">4 000 €</span> sur le compte
          Fortuneo associé à la carte
        </p>
      ),
    },
    {
      Icon: IconCb,
      content: (
        <p className="text-085 leading-alternate">Débit immédiat ou différé </p>
      ),
    },
    {
      Icon: IconVerified,
      content: (
        <p className="text-085 leading-alternate">
          Garanties d’assurance et d’assistance optimales
        </p>
      ),
    },
    {
      Icon: IconPublish,
      content: (
        <p className="text-085 leading-alternate">
          Plafond de paiement/retrait les plus élevés
        </p>
      ),
    },
    {
      Icon: IconRoomService,
      content: (
        <p className="text-085 leading-alternate">
          Conciergerie, services en aéroport, téléconsultation médicale,...
        </p>
      ),
    },
  ],
})

export const worldEliteShared: CardDataFunc = (legalMention) => ({
  mobileMenu: {
    id: 'world-elite',
    label: 'World Elite',
  },
  general: {
    name: 'World Elite CB Mastercard',
    assetName: 'worldElite',
    underCondition: (
      <>
        Dès 5 500 € de revenus nets mensuels à justifier à deux à la
        souscription
      </>
    ),
    link: '/compte-bancaire/carte-world-elite-mastercard',
    clientLink: '/compte-bancaire/carte-world-elite-mastercard-prive',
  },
  advantages: [
    {
      Icon: IconZeroEuro,
      content: (
        <p className="text-085 leading-alternate">
          Gratuite
          <LegalMention legalMention={legalMention} />, à partir d’un versement
          mensuel de <span className="inline-block">4 000 €</span> sur le compte
          Fortuneo associé à la carte
        </p>
      ),
    },
    {
      Icon: IconCb,
      content: (
        <p className="text-085 leading-alternate">Débit immédiat ou différé </p>
      ),
    },
    {
      Icon: IconVerified,
      content: (
        <p className="text-085 leading-alternate">
          Garanties d’assurance et d’assistance optimales
        </p>
      ),
    },
    {
      Icon: IconPublish,
      content: (
        <p className="text-085 leading-alternate">
          Plafond de paiement/retrait les plus élevés
        </p>
      ),
    },
    {
      Icon: IconRoomService,
      content: (
        <p className="text-085 leading-alternate">
          Conciergerie, services en aéroport, téléconsultation médicale,...
        </p>
      ),
    },
  ],
})
